// @workflow platform.viewer, assistant.send-message
/**
 * The document view: the facility page's 'doc' pane, where P&IDs, drawings and reports (PDF or
 * image) open in their own Forge viewer (`store.state.pdfviewer`) with their markup links drawn
 * on top (forgeviewer.js launchPDFViewer). Every operation forwards to today's implementation.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 *
 * `deps.documents` (legacy.js):
 *   isDrawingFile(name) → boolean   the file opens in the document view (forgeviewer isPdfViewer / isImageViewer)
 *   openPage(page)                  forgeviewer.pdfViewerOpenPageByNumber
 *   renderedTagIds(viewer) → string[]   tag ids of the link elements drawn now
 *   highlightTags(tagIds, viewer) → string[]   forgeviewer.highlightTagLinkElements: the ids now lit
 *   showLink(link, viewer)          forgeviewer.ensureDocLinkVisible: pan to a link only when off screen
 */
import { isPaneVisible } from './views.js';

/** The pane type of the document view (forgeviewer.js openAttachment: openViewPageWithType('doc')). */
export const PANE_TYPE_DOC = 'doc';
/** MainView's four view slots (store/modules/viewers.js view1Type..view4Type). */
const VIEW_SLOTS = Object.freeze([1, 2, 3, 4]);

/** The markup links of an attachment as `{ page, tagId }` (links without a tag left out). */
function linksOf(attachment) {
  const pages = (attachment && Array.isArray(attachment.pagesMarkups)) ? attachment.pagesMarkups : [];
  const links = [];
  pages.forEach((page) => {
    (Array.isArray(page && page.links) ? page.links : []).forEach((link) => {
      if (link && link.tagId) links.push({ page: Number(page.pageId) || null, tagId: String(link.tagId), link });
    });
  });
  return links;
}

/** @param {object} ctx see core.js `createViewer` */
export function createDocumentOps(ctx) {
  const { store, deps } = ctx;
  const docs = deps.documents;
  const viewerOf = () => store.state.pdfviewer || null;
  const attachmentOf = () => store.getters.getActiveAttachment || null;

  return {
    /** The file opens in the document view (PDF or image); anything else goes to another pane or tab. */
    isDrawingFile: (fileName) => !!docs.isDrawingFile(fileName),
    /**
     * The page has a document pane, or a free slot for one. Without either, MainView's
     * openViewPageWithType only warns the user that every view is in use and opens nothing.
     */
    documentPaneRoom() {
      const types = VIEW_SLOTS.map((slot) => store.getters[`GET_VIEW_${slot}_TYPE`]);
      return types.some((type) => type === PANE_TYPE_DOC || !type);
    },
    /**
     * The store's OPEN_ATTACHMENT_IN_VIEWER: the document's markups (links per page) are loaded,
     * then forgeviewer.openAttachment opens the 'doc' pane and starts loading the file there.
     * Resolves once the document is the active one, before its pages are drawn.
     */
    openDocument: ({ documentId, storageReference, fileName }) =>
      store.dispatch('OPEN_ATTACHMENT_IN_VIEWER', { documentId, storageReference, fileName }),
    openDocumentPage: (page) => docs.openPage(page),
    /**
     * What the document view shows now. `model` is the loaded page (its identity tells a new load
     * from the old one); `links` are every page's tag links from the document's markups;
     * `renderedTagIds` the tags whose links are drawn on screen.
     */
    documentView() {
      const viewer = viewerOf();
      const attachment = attachmentOf();
      const model = (viewer && viewer.model) || null;
      return {
        documentId: attachment ? attachment.referenceDocumentId || attachment.documentId || null : null,
        loaded: !!store.state.pdfViewerLoaded && !!model,
        model,
        page: Number(store.getters.getActiveAttachmentPage) || null,
        pageCount: Number(store.getters.getActiveAttachmentPagesCount) || 0,
        links: linksOf(attachment).map(({ page, tagId }) => ({ page, tagId })),
        renderedTagIds: viewer ? docs.renderedTagIds(viewer) : [],
      };
    },
    /**
     * The document open on screen as `{ id, name }` (the assistant's "the report", "this drawing"), or null: the active
     * document of the document view, only while its pane is visible (like the 360 in views.js openScan). `name` is its file
     * name. The page shown is in documentView().
     */
    shownDocument() {
      if (!isPaneVisible(store.state.mainApp, PANE_TYPE_DOC)) return null;
      const attachment = attachmentOf();
      const id = attachment ? attachment.referenceDocumentId || attachment.documentId || null : null;
      if (id == null || id === '') return null;
      return { id: String(id), name: attachment.name || null };
    },
    /**
     * Lights every drawn link of these tags (the others are cleared) and pans to the first one when
     * it is off screen. Returns the tag ids, as given, that now show lit.
     */
    highlightDocumentTags(tagIds) {
      const viewer = viewerOf();
      if (!viewer) return [];
      const lit = docs.highlightTags(tagIds, viewer);
      const page = Number(store.getters.getActiveAttachmentPage) || null;
      const litKeys = new Set(lit.map((id) => String(id).toLowerCase()));
      const first = linksOf(attachmentOf()).find((entry) => entry.page === page && litKeys.has(entry.tagId.toLowerCase()));
      if (first) docs.showLink(first.link, viewer);
      return lit;
    },
  };
}
